// =============================================================================
// AttachDesignCard — manual design attachment for unlinked order lines
// -----------------------------------------------------------------------------
// Recovery surface for incident SH-238897: a paid order that ingested with
// stripped cart properties has STOREFRONT lines with no design linkage and no
// print files. This card lists those lines and opens a modal where staff look
// up a design (by internal id or public DR_ reference), verify it, and attach
// it. The API re-stamps the same coherent field set as ingest and re-enters
// the export pipeline.
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient, ApiClientError } from '@tkpod/api-client';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  ModalFooter,
  Select,
  Spinner,
} from '@tkpod/ui';

// ---------------------------------------------------------------------------
// Types (mirror the admin design detail API)
// ---------------------------------------------------------------------------

interface DesignPartView {
  id: string;
  partKey: string;
  kind: string;
  previewStatus: string;
  exportStatus: string;
  previewUrl: string | null;
}

interface DesignOrderItemView {
  id: string;
  orderId: string;
  orderNumber: string;
}

interface DesignDetail {
  id: string;
  designRefId: string | null;
  title: string | null;
  mode: string;
  context: string;
  status: string;
  isLocked: boolean;
  lockedByOrderId: string | null;
  cartBridgeAt: string | null;
  previewUrl: string | null;
  organization: { id: string; name: string };
  createdAt: string;
  parts: DesignPartView[];
  orderItems: DesignOrderItemView[];
}

export interface UnlinkedLine {
  id: string;
  title: string;
  variantTitle: string | null;
  quantity: number;
  sku: string | null;
}

interface AttachDesignCardProps {
  orderId: string;
  organizationId: string;
  lines: UnlinkedLine[];
  canAttach: boolean;
  onSuccess: () => void;
  onFeedback: (type: 'success' | 'error', text: string) => void;
}

const CUID_REGEX = /^c[a-z0-9]{24}$/;
const DESIGN_REF_REGEX = /^DR_[A-HJ-NP-Za-km-z2-9]{14}$/;

function isLookupIdValid(value: string): boolean {
  return CUID_REGEX.test(value) || DESIGN_REF_REGEX.test(value);
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function AttachDesignCard({
  orderId,
  organizationId,
  lines,
  canAttach,
  onSuccess,
  onFeedback,
}: AttachDesignCardProps) {
  const [modalLine, setModalLine] = useState<UnlinkedLine | null>(null);

  if (lines.length === 0) return null;

  return (
    <Card className="border-danger/40">
      <CardHeader title="Lines missing design linkage" />
      <div className="px-4 pb-2 sm:px-5">
        <p className="text-xs text-text-secondary">
          These custom-design lines ingested without a design reference — no
          print files can be produced until a design is attached. Find the
          customer&apos;s design (the Anomaly Center lists orphan candidates)
          and attach it here.
        </p>
      </div>
      <div className="divide-y divide-border">
        {lines.map((line) => (
          <div
            key={line.id}
            className="flex items-center justify-between gap-3 px-4 py-2.5 sm:px-5"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-text-primary">
                {line.title}
              </p>
              <p className="truncate text-xs text-text-tertiary">
                {[line.variantTitle, line.sku, `× ${line.quantity}`]
                  .filter(Boolean)
                  .join(' • ')}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant="danger" size="sm">
                No design
              </Badge>
              {canAttach && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setModalLine(line)}
                >
                  Attach design
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {modalLine && (
        <AttachDesignModal
          orderId={orderId}
          organizationId={organizationId}
          line={modalLine}
          onClose={() => setModalLine(null)}
          onSuccess={() => {
            setModalLine(null);
            onSuccess();
          }}
          onFeedback={onFeedback}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Modal — look up a design, verify, attach
// ---------------------------------------------------------------------------

function AttachDesignModal({
  orderId,
  organizationId,
  line,
  onClose,
  onSuccess,
  onFeedback,
}: {
  orderId: string;
  organizationId: string;
  line: UnlinkedLine;
  onClose: () => void;
  onSuccess: () => void;
  onFeedback: (type: 'success' | 'error', text: string) => void;
}) {
  const api = useApiClient();
  const [lookupInput, setLookupInput] = useState('');
  const [lookupId, setLookupId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // Explicit part selection — needed for multi-sheet orders where each line
  // maps to a different sheet of the SAME design. Empty = server derives it.
  const [partKey, setPartKey] = useState('');

  const trimmed = lookupInput.trim();
  const inputValid = isLookupIdValid(trimmed);

  const designQuery = useQuery({
    queryKey: ['admin', 'designs', lookupId],
    queryFn: () =>
      api.get<{ data: DesignDetail }>(`/api/v1/admin/designs/${lookupId}`),
    enabled: !!lookupId,
    retry: false,
  });

  const design = designQuery.data?.data ?? null;
  const wrongOrg = !!design && design.organization.id !== organizationId;
  const lockedElsewhere =
    !!design &&
    design.isLocked &&
    !!design.lockedByOrderId &&
    design.lockedByOrderId !== orderId;
  const referencedElsewhere =
    !!design && design.orderItems.some((i) => i.orderId !== orderId);
  const canSubmit =
    !!design && !wrongOrg && !lockedElsewhere && !referencedElsewhere;

  const attachMutation = useMutation({
    mutationFn: () =>
      api.post(
        `/api/v1/admin/orders/${orderId}/items/${line.id}/attach-design`,
        {
          ...(design!.designRefId
            ? { designRefId: design!.designRefId }
            : { designId: design!.id }),
          ...(partKey ? { partKey } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      ),
    onSuccess: () => {
      onFeedback('success', 'Design attached — exports re-enqueued.');
      onSuccess();
    },
    onError: (err: Error) => {
      const message =
        err instanceof ApiClientError && err.apiError?.details?.length
          ? err.apiError.details.map((d) => d.message).join('. ')
          : err.message || 'Failed to attach design.';
      onFeedback('error', message);
    },
  });

  const busy = attachMutation.isPending;

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title={`Attach design — ${line.title}`}
      description="Look up the design by its internal id or public DR_ reference, verify it belongs to this order's customer, then attach. The line's print files render automatically afterwards."
      size="md"
    >
      <div className="space-y-4">
        {/* Lookup input */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label
              htmlFor="attach-design-lookup"
              className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
            >
              Design id or reference
            </label>
            <Input
              id="attach-design-lookup"
              value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)}
              placeholder="DR_… or internal design id"
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputValid) setLookupId(trimmed);
              }}
            />
          </div>
          <Button
            variant="secondary"
            disabled={!inputValid || busy}
            onClick={() => setLookupId(trimmed)}
          >
            Look up
          </Button>
        </div>
        {trimmed.length > 0 && !inputValid && (
          <p className="text-xs text-danger">
            Enter a valid design reference (DR_ + 14 characters) or internal
            design id.
          </p>
        )}

        {/* Lookup result */}
        {designQuery.isFetching ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner />
          </div>
        ) : designQuery.isError ? (
          <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
            {designQuery.error instanceof Error &&
            designQuery.error.message.length > 0
              ? designQuery.error.message
              : 'Design not found.'}
          </p>
        ) : design ? (
          <div className="space-y-3 rounded-md border border-border bg-surface-secondary p-3">
            <div className="flex items-start gap-3">
              {design.previewUrl ? (
                <img
                  src={design.previewUrl}
                  alt="Design preview"
                  className="h-16 w-16 shrink-0 rounded-md border border-border object-contain"
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-border text-[10px] text-text-tertiary">
                  No preview
                </div>
              )}
              <div className="min-w-0 space-y-0.5 text-xs">
                <p className="truncate font-medium text-text-primary">
                  {design.title ?? design.designRefId ?? design.id}
                </p>
                <p className="font-mono text-text-secondary">
                  {design.designRefId ?? design.id}
                </p>
                <p className="text-text-tertiary">
                  {design.mode} • {design.status} • {design.parts.length} part
                  {design.parts.length === 1 ? '' : 's'} • created{' '}
                  {new Date(design.createdAt).toLocaleString()}
                </p>
                <p className="text-text-tertiary">
                  Org: {design.organization.name}
                  {design.cartBridgeAt
                    ? ` • cart hand-off ${new Date(design.cartBridgeAt).toLocaleString()}`
                    : ' • never reached cart hand-off'}
                </p>
              </div>
            </div>

            {wrongOrg && (
              <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">
                This design belongs to a different organization (
                {design.organization.name}) and cannot be attached to this
                order.
              </p>
            )}
            {lockedElsewhere && (
              <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">
                This design is locked by another order and cannot be attached.
              </p>
            )}
            {referencedElsewhere && (
              <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">
                This design is already referenced by another order (
                {design.orderItems
                  .filter((i) => i.orderId !== orderId)
                  .map((i) => i.orderNumber)
                  .join(', ')}
                ) and cannot be attached.
              </p>
            )}
          </div>
        ) : null}

        {/* Part selection — required knowledge for multi-sheet designs */}
        {canSubmit && design.parts.length > 0 && (
          <Select
            label="Design part"
            value={partKey}
            onChange={(e) => setPartKey(e.target.value)}
            disabled={busy}
            options={[
              { value: '', label: 'Auto (derived from the line)' },
              ...design.parts.map((p) => ({
                value: p.partKey,
                label: `${p.partKey} (${p.kind})`,
              })),
            ]}
          />
        )}

        {/* Note */}
        {canSubmit && (
          <div>
            <label
              htmlFor="attach-design-note"
              className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
            >
              Note (optional)
            </label>
            <Input
              id="attach-design-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="e.g. Orphan candidate from Anomaly Center"
              disabled={busy}
            />
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          disabled={!canSubmit || busy}
          loading={busy}
          onClick={() => attachMutation.mutate()}
        >
          Attach design
        </Button>
      </ModalFooter>
    </Modal>
  );
}
